import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { Page, SectionTitle, Card, StatusBadge, StatStrip, Segmented, Loading, Label11, usd, int, plural } from "@/components/common";
import { TextScramble } from "@/components/Motion";
import { EngineWithLogo } from "@/components/ProviderLogo";
import ChangeReport, { FlagList, FileRow } from "@/components/ChangeReport";
import { MoneyCard, TermsCard } from "@/components/ObjectiveMoney";
import { HireForObjective, FeeDialog } from "@/components/HiringTerms";
import { Button } from "@/components/ui/button";
import AssistantButton, { useAssistantShown } from "@/components/AssistantButton";
import { dueLabel } from "@/pages/owner/Objectives";
import { APP_NAME } from "@/config";
import { cn } from "@/lib/utils";
import { useFlags } from "@/lib/flags";
import { investigatePath } from "@/components/InvestigateTimeline";
import { ArrowLeft, Users, GitPullRequest, ScanSearch, BriefcaseBusiness } from "lucide-react";

// design_handoff_crewbench/screens/Objectives.dc.html, the detail view: everything done for one objective, across all
// its sessions (backend: project_routes.objective_detail). Its Money and Hiring terms cards and "Hire someone for this"
// are design_handoff_team_roles/ (components/ObjectiveMoney.jsx).
const STATUSES = [["todo", "To do"], ["in progress", "In progress"], ["review", "In review"], ["done", "Done"]];
const and = (names) => (names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`);

function SessionRow({ s, selected, onPick }) {
  const r = s.report;
  const flagged = r ? r.flags.reduce((a, f) => a + f.paths.length, 0) : 0;
  // Investigate (FEATURE_INVESTIGATE): beside the row's button, never inside it
  const investigate = useFlags().investigate;
  return (
    <div className="relative bg-card">
      <button type="button" disabled={!r} onClick={onPick} data-testid={`objective-session-${s.short_id}`}
        className={cn("relative w-full text-left bg-card flex flex-col gap-1 px-4 py-3 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring",
          r ? "cursor-pointer hover:bg-gr-hover" : "cursor-default", investigate && "pb-9 sm:pb-3 sm:pr-28")}>
        {selected && <span className="absolute inset-0 bg-gr-hover-strong pointer-events-none" aria-hidden="true" />}
        <span className="flex flex-wrap items-center gap-2.5">
          <span className="font-semibold tabular-nums"><TextScramble>{s.short_id}</TextScramble></span>
          <EngineWithLogo engine={s.engine} className="gap-1.5 text-sm [&_svg]:w-3.5 [&_svg]:h-3.5" />
          <StatusBadge status={s.status} live />
          {s.pull_request?.number && (
            <span className="flex items-center gap-1 text-sm text-muted-foreground tabular-nums"><GitPullRequest className="w-3.5 h-3.5" />#{s.pull_request.number}</span>
          )}
          <span className="ml-auto text-sm tabular-nums">{usd(s.spent_usd)}</span>
        </span>
        <span className="text-sm text-muted-foreground tabular-nums">
          {s.coder || "A former member"} · {r ? <>{plural(r.totals.files, "file")} <span className="text-gr-success">+{int(r.totals.added_lines)}</span> <span className="text-gr-danger">−{int(r.totals.removed_lines)}</span></>
            : "No change report until it's submitted or ends"}
          {flagged > 0 && <span className="text-gr-warning"> · {flagged} to check</span>}
        </span>
      </button>
      {investigate && (
        <Link to={investigatePath("owner", s.id)} data-testid={`objective-session-investigate-${s.short_id}`}
          className="absolute right-4 bottom-3 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm">
          <ScanSearch className="w-3.5 h-3.5" />Investigate
        </Link>
      )}
    </div>
  );
}

export default function ObjectiveDetail() {
  const { oid } = useParams();
  const qc = useQueryClient();
  const assistant = useAssistantShown();
  const { data, isLoading, error } = useQuery({
    queryKey: ["objective", oid], queryFn: async () => (await api.get(`/objectives/${oid}`)).data, refetchInterval: 15000,
  });
  const [selected, setSelected] = useState(null);
  const [hiring, setHiring] = useState(false);
  const [feeFor, setFeeFor] = useState(null);
  const from = useLocation().state?.back;  // the Money view, with its month and project
  useEffect(() => { window.scrollTo(0, 0); }, [oid]);  // the view changed: back to the top
  const move = useMutation({
    mutationFn: async (status) => api.patch(`/objectives/${oid}`, { status }),
    onSuccess: () => ["objective", "objectives"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
    onError: (e) => toast.error(apiErr(e)),
  });

  const back = (
    <Link to={from || "/founder/objectives"} className="inline-flex items-center gap-1.5 self-start text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="w-3.5 h-3.5" />Objectives
    </Link>
  );
  if (isLoading) return <Page>{back}<Loading /></Page>;
  if (error) return <Page>{back}<Card className="px-5 py-6 text-muted-foreground">This objective doesn't exist, or isn't your company's.</Card></Page>;

  const { objective: o, project, totals: t, sessions, files, flags, money: m } = data;
  const withReports = sessions.filter((s) => s.report);
  const current = sessions.find((s) => s.id === selected && s.report) || withReports[0];
  const active = sessions.filter((s) => s.status === "active").length;
  const prs = sessions.filter((s) => s.pull_request?.number).length;
  const submitted = sessions.filter((s) => s.review_ready).length;
  const people = o.assignees.map((a) => a.name || "a former member");
  const status = move.isPending ? move.variables : o.status;
  const word = (STATUSES.find(([v]) => v === o.status) || STATUSES[0])[1];
  const taken = [...o.assignees.map((a) => a.id), ...m.terms.map((x) => x.person.id).filter(Boolean)];

  return (
    <Page data-testid="objective-detail">
      {back}
      <header className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex flex-col gap-1.5 min-w-0">
          <Label11>{[project?.name || "No project", "Objective", o.due_date && `Due ${dueLabel(o.due_date)}`].filter(Boolean).join(" · ")}</Label11>
          <h1 className="m-0 font-display text-[28px] leading-[1.2] tracking-[0.02em] font-semibold [text-wrap:balance]">
            <span className="block font-normal text-muted-foreground">{o.title}</span>
            <span className="block">{word}. {m.total_usd ? `${usd(m.total_usd)} total cost so far.` : "Nothing spent yet."}</span>
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {assistant && <AssistantButton />}
          <Segmented label="Status" options={STATUSES} value={status} onChange={(v) => v !== o.status && move.mutate(v)} testid="objective-status" />
          {o.status !== "done" && <Button variant="outline" onClick={() => setHiring(true)} data-testid="objective-hire"><BriefcaseBusiness />Hire someone for this</Button>}
        </div>
      </header>

      <Card className="px-5 flex flex-col gap-3">
        <p className="m-0 text-gr-ink-secondary [text-wrap:pretty]">{o.description || "No description yet."}</p>
        <p className="m-0 text-gr-ink-secondary [text-wrap:pretty]"><span className="font-semibold text-foreground">Done when:</span> {o.success_criteria || "Not written yet."}</p>
        <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Users className="w-3.5 h-3.5" />{people.length ? `Assigned to ${and(people)}` : "Nobody is assigned yet"}
        </span>
      </Card>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(420px,100%),1fr))] gap-4 items-start">
        <MoneyCard m={m} />
        <TermsCard m={m} onChangeFee={(x) => setFeeFor({ task: { ...x, objective: o.title }, person: x.person })} />
      </div>

      <StatStrip min={180} cellClassName="px-5 py-4 gap-2" items={[
        { label: "Sessions", value: int(t.sessions), huge: true, sub: active ? `${active} active` : "None active" },
        { label: "Commits", value: int(t.commits), huge: true, sub: sessions.length ? `on ${plural(sessions.length, "branch", "branches")}` : "None yet" },
        { label: "Lines added", value: int(t.lines_added), huge: true, sub: `${int(t.lines_removed)} removed` },
        { label: "Pull requests", value: int(prs), huge: true, sub: submitted ? `${submitted} submitted for review` : "None submitted" },
      ]} />

      {flags.length > 0 && <Card className="px-5" data-testid="objective-flags"><FlagList flags={flags} className="gap-2" /></Card>}

      <div className="flex flex-wrap gap-5 items-start">
        <section className="flex-[1_1_420px] min-w-0 flex flex-col gap-3">
          <SectionTitle note={withReports.length ? "Pick one to see its change report" : ""}>Sessions</SectionTitle>
          {sessions.length ? (
            <div className="flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden">
              {sessions.map((s) => <SessionRow key={s.id} s={s} selected={current?.id === s.id} onPick={() => setSelected(s.id)} />)}
            </div>
          ) : (
            <Card className="px-5 py-6 text-muted-foreground [text-wrap:pretty]">No sessions yet. They show up here once someone starts a session for this objective.</Card>
          )}
        </section>
        <section className="flex-[1_1_420px] min-w-0 flex flex-col gap-3">
          <SectionTitle note={files.length ? plural(files.length, "file") : ""}>Every file changed</SectionTitle>
          {files.length ? (
            <div className="flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden text-sm" data-testid="objective-files">
              {files.map((f) => <FileRow key={f.path} f={f} className="px-4" extra={<span className="text-muted-foreground"> · {f.sessions.join(", ")}</span>} />)}
            </div>
          ) : <Card className="px-5 py-6 text-muted-foreground">No changes reported yet.</Card>}
        </section>
      </div>

      {current && (
        <section className="flex flex-col gap-3">
          <SectionTitle note={`Built by ${APP_NAME} from git. Nobody can edit it.`}>Change report · session {current.short_id}</SectionTitle>
          <Card className="p-5"><ChangeReport report={current.report} pullRequest={current.pull_request} /></Card>
        </section>
      )}
      <HireForObjective objective={o} taken={taken} open={hiring} onOpenChange={setHiring} />
      <FeeDialog task={feeFor?.task} person={feeFor?.person} onOpenChange={(v) => !v && setFeeFor(null)} />
    </Page>
  );
}
